import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Search, ChevronDown, X, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";

/**
 * CustomerSelector - A reusable customer selector with inline search and create functionality
 * 
 * @param {Object} props
 * @param {Array} props.customers - Array of customer objects
 * @param {string} props.value - Selected customer ID
 * @param {Function} props.onChange - Callback when customer is selected
 * @param {Function} props.onCustomerCreated - Callback when a new customer is created
 * @param {string} props.placeholder - Placeholder text
 */
export const CustomerSelector = ({
  customers = [],
  value,
  onChange,
  onCustomerCreated,
  placeholder = "Sök eller välj kund...",
  "data-testid": testId,
}) => {
  const { token } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    org_number: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    contact_person: "",
  });

  // Get selected customer
  const selectedCustomer = customers.find(c => c.id === value);
  
  // Filter customers based on search term (live search)
  const filteredCustomers = customers.filter(c => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      c.name?.toLowerCase().includes(search) ||
      c.org_number?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.city?.toLowerCase().includes(search)
    );
  });

  // Reset highlighted index when search changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredCustomers.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case "Enter":
        e.preventDefault();
        if (filteredCustomers[highlightedIndex]) {
          handleSelectCustomer(filteredCustomers[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
    }
  };

  const handleSelectCustomer = (customer) => {
    onChange(customer.id);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSearchTerm("");
  };

  const resetForm = () => {
    setFormData({
      name: "",
      org_number: "",
      email: "",
      phone: "",
      address: "",
      postal_code: "",
      city: "",
      contact_person: "",
      vat_registered: null,
      employer_registered: null,
      company_type: null,
    });
  };

  // Auto-lookup when org number is complete (10 digits)
  const handleOrgNumberChange = (value) => {
    setFormData({ ...formData, org_number: value });
    const cleanOrg = value.replace(/[-\s]/g, '');
    if (cleanOrg.length === 10 && /^\d{10}$/.test(cleanOrg)) {
      lookupCompany(value);
    }
  };

  const lookupCompany = async (orgNumber) => {
    if (!orgNumber) return;
    
    setLookupLoading(true);
    try {
      const response = await axios.get(`${API}/customers/lookup/${orgNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({
        ...prev,
        name: response.data.name || prev.name,
        address: response.data.address || prev.address,
        postal_code: response.data.postal_code || prev.postal_code,
        city: response.data.city || prev.city,
        vat_registered: response.data.vat_registered,
        employer_registered: response.data.employer_registered,
        company_type: response.data.company_type,
      }));
      toast.success("Företagsinformation hämtad");
    } catch (error) {
      // Silent fail for auto-lookup
    } finally {
      setLookupLoading(false);
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("Ange kundnamn");
      return;
    }

    setCreateLoading(true);
    try {
      const response = await axios.post(`${API}/customers`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Kund skapad!");
      setShowCreateDialog(false);
      resetForm();
      
      // Call callback with new customer
      if (onCustomerCreated) {
        onCustomerCreated(response.data);
      }
      
      // Auto-select the new customer
      if (onChange && response.data.id) {
        onChange(response.data.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa kund");
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      <div className="flex gap-2">
        <div className="flex-1 relative" ref={dropdownRef}>
          {/* Search/Select Input */}
          <div 
            className="flex items-center border rounded-md bg-white cursor-pointer hover:border-slate-400 transition-colors"
            onClick={() => {
              setIsOpen(true);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
          >
            <div className="flex-1 flex items-center">
              {isOpen ? (
                <Input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid={`${testId}-search`}
                />
              ) : (
                <div className="px-3 py-2 flex-1 text-sm" data-testid={testId}>
                  {selectedCustomer ? (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{selectedCustomer.name}</span>
                      {selectedCustomer.org_number && (
                        <span className="text-xs text-slate-400 font-mono">({selectedCustomer.org_number})</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-500">{placeholder}</span>
                  )}
                </div>
              )}
            </div>
            {value && !isOpen && (
              <button
                type="button"
                onClick={handleClear}
                className="p-2 hover:text-red-500 transition-colors"
              >
                <X size={14} />
              </button>
            )}
            <div className="px-2 text-slate-400">
              <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>

          {/* Dropdown */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map((customer, index) => (
                  <div
                    key={customer.id}
                    className={`px-3 py-2 cursor-pointer flex items-center justify-between ${
                      index === highlightedIndex ? 'bg-indigo-50 text-indigo-900' : 'hover:bg-slate-50'
                    } ${customer.id === value ? 'bg-indigo-100' : ''}`}
                    onClick={() => handleSelectCustomer(customer)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <div>
                      <div className="font-medium text-sm">{customer.name}</div>
                      {(customer.org_number || customer.city) && (
                        <div className="text-xs text-slate-500">
                          {customer.org_number && <span className="font-mono">{customer.org_number}</span>}
                          {customer.org_number && customer.city && <span> • </span>}
                          {customer.city && <span>{customer.city}</span>}
                        </div>
                      )}
                    </div>
                    {customer.id === value && (
                      <div className="text-indigo-600 text-xs font-medium">Vald</div>
                    )}
                  </div>
                ))
              ) : (
                <div className="px-3 py-4 text-center text-slate-500 text-sm">
                  <p>Inga kunder hittades</p>
                  {searchTerm && (
                    <p className="text-xs mt-1">Sök: "{searchTerm}"</p>
                  )}
                </div>
              )}
              
              {/* Create new option at bottom */}
              <div 
                className="px-3 py-2 border-t bg-slate-50 cursor-pointer hover:bg-slate-100 flex items-center gap-2 text-indigo-600"
                onClick={() => {
                  setIsOpen(false);
                  setShowCreateDialog(true);
                }}
              >
                <UserPlus size={16} />
                <span className="text-sm font-medium">Skapa ny kund</span>
              </div>
            </div>
          )}
        </div>
        <Button 
          type="button" 
          variant="outline" 
          size="icon"
          onClick={() => setShowCreateDialog(true)}
          title="Skapa ny kund"
          data-testid="create-customer-inline-btn"
        >
          <UserPlus size={18} />
        </Button>
      </div>

      {/* Create Customer Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={20} />
              Skapa ny kund
            </DialogTitle>
            <DialogDescription>
              Fyll i kunduppgifter. Org.nummer hämtar info automatiskt.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCustomer} className="space-y-4">
            {/* Org number with auto-lookup */}
            <div className="flex gap-2">
              <div className="flex-1 space-y-2">
                <Label htmlFor="inline-org_number">Organisationsnummer</Label>
                <Input
                  id="inline-org_number"
                  value={formData.org_number}
                  onChange={(e) => handleOrgNumberChange(e.target.value)}
                  placeholder="556123-4567"
                  data-testid="inline-customer-org-number"
                />
                <p className="text-xs text-slate-500">Info hämtas automatiskt vid 10 siffror</p>
              </div>
              <div className="flex items-end pb-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => lookupCompany(formData.org_number)}
                  disabled={lookupLoading}
                >
                  {lookupLoading ? (
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                  ) : (
                    <Search size={16} />
                  )}
                </Button>
              </div>
            </div>

            {/* VAT and Employer Registration Status */}
            {(formData.vat_registered !== null || formData.employer_registered !== null || formData.company_type) && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-2 border border-slate-200">
                <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Registreringsstatus</p>
                <div className="flex flex-wrap gap-4">
                  {formData.vat_registered !== null && (
                    <div className="flex items-center gap-2">
                      {formData.vat_registered ? (
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                          <Check size={12} className="text-green-600" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                          <X size={12} className="text-red-600" />
                        </div>
                      )}
                      <span className={`text-sm ${formData.vat_registered ? 'text-green-700' : 'text-red-700'}`}>
                        Momsregistrerad
                      </span>
                    </div>
                  )}
                  {formData.employer_registered !== null && (
                    <div className="flex items-center gap-2">
                      {formData.employer_registered ? (
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                          <Check size={12} className="text-green-600" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center">
                          <AlertCircle size={12} className="text-amber-600" />
                        </div>
                      )}
                      <span className={`text-sm ${formData.employer_registered ? 'text-green-700' : 'text-amber-700'}`}>
                        {formData.employer_registered ? 'Registrerad arbetsgivare' : 'Ej arbetsgivare'}
                      </span>
                    </div>
                  )}
                </div>
                {formData.company_type && (
                  <p className="text-xs text-slate-500">Bolagsform: {formData.company_type}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="inline-name">Företagsnamn / Kundnamn *</Label>
              <Input
                id="inline-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Företag AB"
                required
                data-testid="inline-customer-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inline-email">E-post</Label>
                <Input
                  id="inline-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="info@foretag.se"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inline-phone">Telefon</Label>
                <Input
                  id="inline-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="08-123 456"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inline-address">Adress</Label>
              <Input
                id="inline-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Gatuadress 123"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inline-postal_code">Postnummer</Label>
                <Input
                  id="inline-postal_code"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  placeholder="123 45"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inline-city">Ort</Label>
                <Input
                  id="inline-city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Stockholm"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inline-contact_person">Kontaktperson</Label>
              <Input
                id="inline-contact_person"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                placeholder="Anna Andersson"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Avbryt
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={createLoading}>
                {createLoading ? "Skapar..." : "Skapa kund"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerSelector;
